import { useState, useEffect, useCallback, useRef } from "react";

const MOBILE_BREAKPOINT = 1024; // px
const FALLBACK_HEIGHT = 800;

const getIsMobile = () => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
};

const getViewportHeight = () => {
    if (typeof window === "undefined") return FALLBACK_HEIGHT;
    // ✅ visualViewport es lo más confiable en iOS con teclado
    return window.visualViewport?.height || window.innerHeight || FALLBACK_HEIGHT;
};

export const useResponsiveModalLayout = () => {
    const [isMobile, setIsMobile] = useState(() => getIsMobile());
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [windowHeight, setWindowHeight] = useState(() => getViewportHeight());
    const scrollLockYRef = useRef(0);

    // ✅ Detectar mobile/desktop
    useEffect(() => {
        if (typeof window === "undefined") return;

        const handleResize = () => {
            const mobile = getIsMobile();
            setIsMobile(mobile);

            if (!mobile) setShowMobileMenu(false);
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ✅ Sincronizar altura en mobile usando visualViewport (iOS + teclado)
    useEffect(() => {
        if (typeof window === "undefined") return;

        const vv = window.visualViewport;

        const update = () => {
            if (!getIsMobile()) return;
            setWindowHeight(getViewportHeight());
        };

        update();

        if (vv) {
            vv.addEventListener("resize", update);
            vv.addEventListener("scroll", update);
            return () => {
                vv.removeEventListener("resize", update);
                vv.removeEventListener("scroll", update);
            };
        } else {
            window.addEventListener("orientationchange", update);
            return () => window.removeEventListener("orientationchange", update);
        }
    }, []);

    // 🔒 Bloquear scroll HORIZONTAL global
    useEffect(() => {
        if (typeof document === "undefined") return;

        const html = document.documentElement;
        const body = document.body;

        const prevHtmlOverflowX = html.style.overflowX;
        const prevBodyOverflowX = body.style.overflowX;
        const prevHtmlWidth = html.style.width;
        const prevBodyWidth = body.style.width;

        html.style.overflowX = "hidden";
        body.style.overflowX = "hidden";
        html.style.width = "100%";
        body.style.width = "100%";

        return () => {
            html.style.overflowX = prevHtmlOverflowX;
            body.style.overflowX = prevBodyOverflowX;
            html.style.width = prevHtmlWidth;
            body.style.width = prevBodyWidth;
        };
    }, []);

    // 🔒 Bloquear scroll VERTICAL cuando menú móvil está abierto
    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") return;

        const body = document.body;

        if (isMobile && showMobileMenu) {
            scrollLockYRef.current = window.scrollY || window.pageYOffset || 0;

            body.style.position = "fixed";
            body.style.top = `-${scrollLockYRef.current}px`;
            body.style.left = "0";
            body.style.right = "0";
            body.style.width = "100%";
            body.style.overflowY = "hidden";
        } else {
            body.style.position = "";
            body.style.top = "";
            body.style.left = "";
            body.style.right = "";
            body.style.width = "";
            body.style.overflowY = "";

            if (scrollLockYRef.current) {
                window.scrollTo(0, scrollLockYRef.current);
                scrollLockYRef.current = 0;
            }
        }

        return () => {
            body.style.position = "";
            body.style.top = "";
            body.style.left = "";
            body.style.right = "";
            body.style.width = "";
            body.style.overflowY = "";
        };
    }, [isMobile, showMobileMenu]);

    // ✅ Utilidad: cerrar teclado
    const dismissKeyboard = useCallback(() => {
        if (typeof document === "undefined") return;
        const el = document.activeElement;
        if (el && typeof el.blur === "function") el.blur();
    }, []);

    const openMobileMenu = useCallback(() => setShowMobileMenu(true), []);
    const closeMobileMenu = useCallback(() => setShowMobileMenu(false), []);

    return {
        isMobile,
        showMobileMenu,
        windowHeight,
        openMobileMenu,
        closeMobileMenu,
        dismissKeyboard,
    };
};
